/* Visuelt – konfigurasjonskartet som levende replika. Samme layoutmotor og nodekomponent som appen (layoutTree · edgePath · Node),
   statisk tre fra skMockData: «Mi konfigurasjon» for BA-V-201 – valgt vei lyser, dokumenterte alternativer står klare,
   det uklare sier «Må avklares» med ord. På telefon: stegliste, ikke krympet kart. */
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Expand, Maximize2, Minus, Plus, RotateCcw, Search } from "lucide-react";
import { Segmented } from "@/components/layout/Segmented";
import { edgePath, layoutTree } from "@/components/canvas/konfig/visuelt/layout";
import { Node } from "@/components/canvas/konfig/visuelt/Node";
import { Ghost, Pill, Primary } from "./SkShell";
import { TREE } from "./skMockData";

const LEGEND = [["valgt", "Valgt", "bg-[hsl(var(--brand))]"], ["avklar", "Må avklares", "bg-[hsl(var(--v-assumed))]"], ["valgbar", "Kan velges", "bg-white border border-black/[0.25]"], ["auto", "Følger automatisk", "bg-black/[0.18]"]];

/* the tree in the shape the app's Node expects */
export const buildTree = () => {
  const index = {};
  const leaf = (b, a, i, selected) => ({ id: `${b.key}-${i}`, label: a.label, part: a.part, extra: a.extra, depth: 2, parentId: b.key, children: [], selected, status: selected ? "valgt" : "valgbar", canChoose: !selected, anyOpen: false });
  const groups = TREE.branches.map((b) => {
    const kids = [...(b.chosen ? [leaf(b, b.chosen, 0, true)] : []), ...b.alts.map((a, i) => leaf(b, a, i + 1, false))];
    const g = { id: b.key, label: b.label, cell: b.cell, depth: 1, parentId: "root", children: kids, selected: Boolean(b.chosen), open: b.open || "", anyOpen: Boolean(b.open), status: b.chosen ? "valgt" : b.open ? "avklar" : "gruppe", chosen: kids.find((k) => k.selected) || null, sub: `${TREE.doc} · ${b.cell}` };
    kids.forEach((k) => { index[k.id] = k; });
    index[g.id] = g;
    return g;
  });
  const root = { id: "root", label: TREE.root.label, sub: TREE.root.sub, depth: 0, parentId: null, children: groups, selected: true, status: "rot", anyOpen: groups.some((g) => g.anyOpen) };
  index.root = root;
  return { root, index, openCount: groups.filter((g) => g.anyOpen).length };
};

const useSize = () => {
  const ref = useRef(null);
  const [sz, setSz] = useState({ w: 800, h: 560 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setSz({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, sz];
};

const noop = () => {};

const Map = ({ marked, expanded, activeId }) => {
  const [ref, { w, h }] = useSize();
  const { pos, edges, bbox } = useMemo(() => layoutTree(marked.root, expanded), [marked, expanded]);
  const s = Math.max(0.5, Math.min(1, (w - 56) / bbox.w, (h - 96) / bbox.h));
  const tx = (w - bbox.w * s) / 2 - bbox.x * s;
  const ty = (h - 56 - bbox.h * s) / 2 - bbox.y * s + 8;
  const nodes = Object.keys(pos).map((id) => marked.index[id]);
  const tone = (e) => {
    const a = marked.index[e.from];
    const b = marked.index[e.to];
    return b.selected && (a.selected || a.depth === 0) ? "sel" : b.anyOpen ? "open" : "base";
  };
  return (
    <div ref={ref} className="relative min-h-0 flex-1 overflow-hidden" data-testid="sk-visuelt-canvas" data-nodes={nodes.length} data-zoom={Math.round(s * 100)}>
      <div className="absolute left-0 top-0" style={{ transform: `translate(${tx}px, ${ty}px) scale(${s})`, transformOrigin: "0 0" }}>
        <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width="1" height="1" aria-hidden="true">
          {edges.map((e) => {
            const t = tone(e);
            return <path key={`${e.from}-${e.to}`} d={edgePath(pos[e.from], pos[e.to])} fill="none" className={t === "sel" ? "stroke-[hsl(var(--brand))]" : t === "open" ? "stroke-[hsl(var(--v-assumed)/0.7)]" : "stroke-black/[0.13]"} strokeWidth={t === "sel" ? 2.25 : 1.5} strokeLinecap="round" />;
          })}
        </svg>
        {nodes.map((n) => <Node key={n.id} n={n} pos={pos[n.id]} active={n.id === activeId} focused={false} expanded={expanded.has(n.id)} dimmed={!n.selected && !n.anyOpen && n.depth > 0} hit={false} onOpen={noop} onToggle={noop} onChoose={noop} summary={TREE.root.code} />)}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3">
        <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1 rounded-[10px] bg-white/85 px-2.5 py-1.5 text-[11.5px] text-muted-foreground backdrop-blur" data-testid="sk-visuelt-legend">
          {LEGEND.map(([k, l, c]) => <li key={k} className="flex items-center gap-1.5"><span className={`inline-block h-2.5 w-2.5 rounded-full ${c}`} />{l}</li>)}
        </ul>
        <div className="ml-auto flex items-center gap-0.5 rounded-[10px] border border-black/[0.06] bg-white/95 p-0.5 text-[12.5px] font-medium text-foreground/75 shadow-[0_6px_20px_-12px_rgba(0,0,0,0.25)] backdrop-blur">
          <span className="flex h-8 min-w-8 items-center justify-center px-1.5"><Minus className="h-3.5 w-3.5" /></span>
          <span className="flex h-8 w-14 items-center justify-center tabular-nums">{Math.round(s * 100)} %</span>
          <span className="flex h-8 min-w-8 items-center justify-center px-1.5"><Plus className="h-3.5 w-3.5" /></span>
          <span className="mx-0.5 h-5 w-px bg-black/[0.08]" />
          <span className="flex h-8 items-center gap-1 px-1.5"><Maximize2 className="h-3.5 w-3.5" /> Tilpass</span>
          <span className="flex h-8 items-center gap-1 px-1.5"><RotateCcw className="h-3.5 w-3.5" /> Tilbakestill</span>
        </div>
      </div>
    </div>
  );
};

const Bar = ({ openCount, compact, ns }) => (
  <div className="flex min-h-[48px] shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-black/[0.06] bg-white px-4 py-1.5" data-testid="sk-visuelt-bar">
    <Ghost icon={ArrowLeft} className="px-2">Ventiler</Ghost>
    <div className="min-w-0 leading-tight"><div className="truncate text-[14px] font-semibold text-foreground">Visuelt · {TREE.root.valve}</div><div className="truncate text-[11.5px] text-muted-foreground">{TREE.root.sub}</div></div>
    <Segmented value="min" onChange={noop} items={[{ key: "kart", label: "Oversiktskart" }, { key: "min", label: "Mi konfigurasjon" }]} size="sm" layoutId={`${ns}-visuelt-mode`} ariaLabel="Visning" className="ml-2" />
    {!compact && <span className="inline-flex h-8 items-center gap-2 rounded-full border border-black/[0.08] bg-white px-3 text-[12.5px] text-muted-foreground"><Search className="h-3.5 w-3.5" /> Søk komponent, delenummer …</span>}
    <Pill status="avklar">{openCount} må avklares</Pill>
    <span className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-medium text-foreground/75"><Expand className="h-3.5 w-3.5" /> Vis alt</span>
  </div>
);

/* the detail panel: what the chosen part means, what it brings, what else is documented – and what a change would do */
const Detail = ({ g }) => {
  const n = g.chosen;
  const alts = g.children.filter((k) => !k.selected);
  const swap = alts[3] || alts[0];
  return (
    <aside className="mb-noscroll flex w-[350px] shrink-0 flex-col overflow-hidden border-l border-black/[0.06] bg-white px-5 py-4" data-testid="sk-visuelt-panel">
      <div className="k-label">{g.label}</div>
      <h2 className="mt-1 text-[16px] font-semibold leading-tight tracking-[-0.01em] text-foreground">{n.label}</h2>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12.5px] text-muted-foreground"><span className="k-ref">{n.part}</span><Pill status="forslag">Valgt</Pill></div>
      <section className="mt-4">
        <div className="k-label mb-1.5">I typekoden</div>
        <div className="rounded-[12px] border border-black/[0.06] px-3 py-2 font-mono text-[13px] text-foreground">RA2 10 H1 0B B N <b className="text-[hsl(var(--brand-strong))]">IB</b></div>
        <p className="k-meta mt-1.5">IB = indikeringsboks med Namur-brytere. Gjelder 14 ventiler av samme produkttype – valget kan gjøres for bare {TREE.root.valve}.</p>
      </section>
      <section className="mt-4">
        <div className="k-label mb-1.5">Komponenter</div>
        <ul className="divide-y divide-black/[0.04] rounded-[12px] border border-black/[0.06]">
          {[{ part: n.part, label: n.label }, ...(n.extra || [])].map((r) => <li key={r.part} className="flex items-baseline gap-3 px-3 py-2 text-[13px]"><span className="font-mono tabular-nums">{r.part}</span><span className="min-w-0 flex-1 truncate text-foreground/80">{r.label}</span></li>)}
        </ul>
        <p className="k-meta mt-1.5">Kilde: {TREE.doc} · celle {g.cell}</p>
      </section>
      <section className="mt-4">
        <div className="k-label mb-1">Dokumenterte alternativer · {alts.length}</div>
        <ul className="divide-y divide-black/[0.05]">
          {alts.map((a) => <li key={a.id} className="flex items-center justify-between gap-2 py-1.5"><div className="min-w-0"><div className="truncate text-[13px] text-foreground">{a.label}</div><div className="k-meta">{a.part}</div></div><span className="shrink-0 rounded-full bg-[hsl(var(--brand))] px-2 py-0.5 text-[11.5px] font-semibold text-white">Velg</span></li>)}
        </ul>
      </section>
      <section className="mt-3 rounded-[12px] bg-[hsl(var(--sidebar))] p-3" data-testid="sk-visuelt-consequence">
        <div className="k-label">Hvis du bytter til «{swap.label}»</div>
        <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground/85">
          <li>Typekode: IB → <span className="font-mono">IC</span> · ny produkttype for 1 ventil</li>
          <li>Stykkliste: − {n.part} · + <span className="font-mono">{swap.part}</span></li>
          <li>Kalkyle: pris venter bekreftelse – vises aldri som 0</li>
          <li>Styring: én analog inngang i fordelerskapet</li>
        </ul>
      </section>
      <div className="mt-auto flex items-center justify-between gap-2 pt-4"><Ghost>Tilbakestill</Ghost><Primary>Lagre valget</Primary></div>
    </aside>
  );
};

/* the phone: the same tree as steps – readable, no shrunken map */
const StepsList = ({ marked }) => (
  <div className="mb-noscroll h-full overflow-hidden px-4 py-4" data-testid="sk-visuelt-steps">
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">Mi konfigurasjon · {TREE.root.valve}</div>
    <div className="mt-1 font-mono text-[15px] font-semibold text-foreground">{TREE.root.code}</div>
    <div className="mt-0.5 text-[12.5px] text-muted-foreground">{TREE.root.sub}</div>
    <ol className="mt-4 divide-y divide-black/[0.06]">
      {marked.root.children.map((g, i) => (
        <li key={g.id} className="py-3">
          <div className="flex items-center justify-between gap-2"><span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{String(i + 1).padStart(2, "0")} {g.label}</span><Pill status={g.chosen ? "forslag" : "avklar"}>{g.chosen ? "Valgt" : "Må avklares"}</Pill></div>
          <div className="mt-1 text-[14px] font-medium text-foreground">{g.chosen ? g.chosen.label : `Må avklares: ${g.open}`}</div>
          <div className="mt-0.5 text-[12px] text-muted-foreground">{g.chosen ? `${g.chosen.part} · ` : ""}{g.children.length - (g.chosen ? 1 : 0)} alternativer dokumentert · {g.cell}</div>
        </li>
      ))}
    </ol>
  </div>
);

export const VisueltBody = ({ nar, mode, ns = "sk-sak" }) => {
  const marked = useMemo(buildTree, []);
  const expanded = useMemo(() => new Set(["root", "ind"]), []);
  if (nar) return <StepsList marked={marked} />;
  const compact = mode === "compact";
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="sk-mock-visuelt">
      <Bar openCount={marked.openCount} compact={compact} ns={ns} />
      <div className="flex min-h-0 flex-1">
        <Map marked={marked} expanded={expanded} activeId="ind-0" />
        {!compact && <Detail g={marked.index.ind} />}
      </div>
    </div>
  );
};
